import { Skeleton } from "@shared/ui/skeleton";
import { routes } from "./routes";
import { languageAgnosticPathKey } from "./lib/route-identity";

/**
 * Is `pathname` a `navFullBleed` route (the Health Assistant, Messages…)? Those
 * pages fill the viewport and own their own scrolling, so a generic "page
 * title + card grid" skeleton would jump when the real, fixed-height layout
 * replaces it. `:param` segments match any single segment; a `/:lang` prefix is
 * ignored. Reads `routes` (the generated table) so there is one source of truth.
 */
export function isFullBleedPath(pathname: string): boolean {
  const target = languageAgnosticPathKey(pathname).split("/").filter(Boolean);
  return routes.some((route) => {
    if (!route.navFullBleed || route.path.startsWith("/:lang")) return false;
    const pattern = route.path.split("/").filter(Boolean);
    return (
      pattern.length === target.length &&
      pattern.every((segment, i) => segment.startsWith(":") || segment === target[i])
    );
  });
}

function currentPathname(): string {
  return typeof window === "undefined" ? "/" : window.location.pathname;
}

/** True when the route about to render is a full-bleed one. */
export function isFullBleedPending(): boolean {
  return isFullBleedPath(currentPathname());
}

/**
 * Chat-shaped skeleton for full-bleed routes: [app nav rail] [conversation
 * list] [header bar / message bubbles / composer]. Columns appear at the same
 * breakpoints as the real layout (nav rail from `lg`, list from `md`), it is
 * exactly viewport tall (`h-dvh`) like the page it stands in for, and — like
 * the generic skeleton — it is decorative: only the sr-only "Loading…" text is
 * announced.
 */
export function FullBleedPendingSkeleton({ loadingLabel }: { loadingLabel: string }) {
  return (
    <div
      className="flex h-dvh w-full overflow-hidden bg-background"
      data-testid="route-pending-fallback"
      data-variant="full-bleed"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="sr-only">{loadingLabel}</span>
      <div
        aria-hidden="true"
        className="flex h-full min-h-0 w-full motion-reduce:[&_*]:animate-none"
      >
        <div className="hidden w-64 shrink-0 space-y-2 border-e p-3 lg:block">
          <Skeleton className="mb-4 h-9 w-full" />
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
        <div className="hidden w-72 shrink-0 space-y-2 border-e p-3 md:block">
          <Skeleton className="mb-3 h-10 w-full" />
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex h-14 shrink-0 items-center gap-3 border-b px-4">
            <Skeleton className="h-6 w-40" />
          </div>
          <div className="min-h-0 flex-1 space-y-4 overflow-hidden p-4">
            <Skeleton className="ms-auto h-10 w-2/3 max-w-sm rounded-2xl" />
            <Skeleton className="h-24 w-4/5 max-w-xl rounded-2xl" />
            <Skeleton className="ms-auto h-10 w-1/2 max-w-xs rounded-2xl" />
            <Skeleton className="h-16 w-3/4 max-w-lg rounded-2xl" />
          </div>
          <div className="shrink-0 border-t p-3">
            <Skeleton className="h-12 w-full rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  );
}
